import { Account, AccountCategory, Client, SelectedAccountOrClient, useDictionary } from '@eih-netbroker/common';
import { ReactComponent as InfoIcon } from '../../../assets/icons/information-fill.svg';
import { useLocale } from '../../../context/NbLocalizationContext';
import { NbButton } from '../../../shared/components/button/Button';
import { FeedbackBox } from '../../../shared/components/feedback-box/FeedbackBox';
import classes from './AccountsPopoverContent.module.scss';

export interface AccountsPopoverContentProps {
  clients: Array<Client>;
  onClose: (result: SelectedAccountOrClient) => void;
  selectAllAccountEnabled: boolean;
  selectedAccountOrClient: SelectedAccountOrClient;
  allowClientSelection: boolean;
  getAccountsOfClient: (clientId: string) => Array<Account>;
  disabledAccountCategories: Array<AccountCategory>;
}

export const AccountsPopoverContent = ({
  clients,
  onClose,
  selectedAccountOrClient,
  allowClientSelection,
  getAccountsOfClient,
  disabledAccountCategories,
}: AccountsPopoverContentProps) => {
  const { translation, translateWithParams } = useLocale();
  const { formatDictionaryType } = useDictionary();

  const selectAccountCategory = (account: Account) => {
    onClose({ account: account, client: undefined });
  };

  const selectAll = (clientId: string) => {
    onClose({ account: undefined, client: clientId, accounts: getAccountsOfClient(clientId) });
  };

  return (
    <>
      {!allowClientSelection && disabledAccountCategories.length === 0 && (
        <FeedbackBox
          icon={<InfoIcon />}
          type={'warning'}
          backgroundBold
          title={translation.header.info}
        />
      )}

      {!allowClientSelection && disabledAccountCategories.length > 0 && (
        <FeedbackBox
          icon={<InfoIcon />}
          type={'warning'}
          backgroundBold
          message={translation.header.infoIfCurrencyExchange}
          className={classes.feedbackBox}
        />
      )}
      <div className={classes.accounts}>
        {clients.map(client => (
          <div
            key={client.clientId}
            className={classes.accountWrapper}
          >
            <div className={classes.account}>
              <p
                data-hj-suppress
                className={classes.accountName}
              >
                {client.clientName}
              </p>

              <div className={classes.accountDetails}>
                <span data-testid="client-id">{client.clientId}</span>
                <span>
                  {translateWithParams(translation.header.accountCount, {
                    accountCount: client.accounts.length.toString(),
                  })}
                </span>
              </div>

              <div className={classes.accountButtons}>
                {client.accounts.length > 1 && (
                  <NbButton
                    data-testid="accounts-popover-content-all-accounts-button"
                    color={selectedAccountOrClient?.client === client.clientId ? 'primary' : 'secondary'}
                    className={classes.accountAllButton}
                    onClick={() => selectAll(client.clientId)}
                    disabled={!allowClientSelection}
                  >
                    {translation.header.allAccounts}
                  </NbButton>
                )}

                {client.accounts.map(account => (
                  <NbButton
                    key={account.accountCategory}
                    data-testid={`accounts-popover-content-account-button-${account.accountCategory}`}
                    color={
                      selectedAccountOrClient?.account?.accountNumber === account.accountNumber
                        ? 'primary'
                        : 'secondary'
                    }
                    onClick={() => selectAccountCategory(account)}
                    disabled={!allowClientSelection && disabledAccountCategories.includes(account.accountCategory)}
                  >
                    {formatDictionaryType(account.accountCategory, 'accountCategoryTypes')}
                  </NbButton>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
